/**
 * Updates navigation menu item classes based on the current page URL.
 * This is necessary for single-page applications using Barba.js, as server-side
 * class generation only happens on the initial load. It ensures that classes like
 * `.current-menu-item` and `.current-menu-ancestor` are correctly applied after
 * a page transition.
 *
 * @param {string} currentUrl - The full URL of the current page.
 * @param {string[]} [menuSelectors=['.header__navigation', '.footer-navigation']] - An array of CSS selectors for the navigation menus to update.
 */
export function updateNavMenuClasses(currentUrl, menuSelectors = ['.header__navigation', '.footer-navigation']) {
    const navMenus = document.querySelectorAll(menuSelectors.join(', '));

    if (!navMenus.length) {
        return;
    }

    try {
        const url = new URL(currentUrl);
        // Normalize the path for comparison: remove trailing slash unless it's the root.
        const currentPath = (url.pathname === '/' ? '/' : url.pathname.replace(/\/$/, "")).toLowerCase();

        navMenus.forEach(nav => {
            const menuLinks = nav.querySelectorAll('li.menu-item a');
            let bestMatches = [];
            let longestMatchLength = -1; // Use -1 to allow matching the root '/' (length 1)

            // First, remove all existing active/ancestor classes to reset the state.
            nav.querySelectorAll('li.menu-item').forEach(li => {
                li.classList.remove(
                    'current-menu-item',
                    'current_page_item',
                    'current-menu-ancestor',
                    'current-page-ancestor'
                );
            });

            // Find the most specific matching link (longest path).
            menuLinks.forEach(link => {
                // Ensure link has a href and is not just a hash link on the same page.
                if (!link.href || link.getAttribute('href').startsWith('#')) return;

                try {
                    const linkUrl = new URL(link.href);
                    // Ignore links to other domains.
                    if (linkUrl.hostname !== url.hostname) return;

                    // Normalize link path for a consistent comparison.
                    const linkPath = (linkUrl.pathname === '/' ? '/' : linkUrl.pathname.replace(/\/$/, "")).toLowerCase();

                    // Check if the current path starts with the link's path.
                    if (currentPath.startsWith(linkPath)) { // Comparison is now case-insensitive
                        // If this match is longer than any previous one, it's the new best.
                        if (linkPath.length > longestMatchLength) {
                            longestMatchLength = linkPath.length;
                            bestMatches = [link]; // Reset the array with this new best match.
                        } else if (linkPath.length === longestMatchLength) {
                            bestMatches.push(link); // It's another match of the same best length.
                        }
                    }
                } catch (e) {
                    // Invalid URL in href, ignore it.
                    console.warn('Nav Updater: Invalid URL found in menu link:', link.href);
                }
            });

            if (bestMatches.length > 0) {
                bestMatches.forEach(bestMatch => {
                    const bestMatchLi = bestMatch.closest('li.menu-item');
                    if (!bestMatchLi) return;

                    const bestMatchUrl = new URL(bestMatch.href);
                    const bestMatchPath = bestMatchUrl.pathname;
                    const normalizedBestMatchPath = (bestMatchPath === '/' ? '/' : bestMatchPath.replace(/\/$/, "")).toLowerCase();

                    // Add ancestor classes to the best match and all its parents.
                    let parentLi = bestMatchLi;
                    while (parentLi) {
                        parentLi.classList.add('current-menu-ancestor', 'current-page-ancestor');
                        parentLi = parentLi.parentElement.closest('li.menu-item');
                    }

                    // If it's an exact match, also add the current item class.
                    if (normalizedBestMatchPath === currentPath) {
                        bestMatchLi.classList.add('current-menu-item', 'current_page_item');
                    }
                });
            }
        });
    } catch (e) {
        console.error('Nav Updater: Could not parse current URL:', currentUrl, e);
    }
}